import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Paperclip, Upload, Eye, Loader2, Archive, XCircle } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const STC = { "Déposé": "bg-blue-100 text-blue-700", "Vérifié": "bg-emerald-100 text-emerald-700", "Refusé": "bg-red-100 text-red-700", "Archivé": "bg-slate-100 text-slate-500" };

export function AttachmentsDialog({ open, onClose, parentType, parentId, types }) {
  const [items, setItems] = useState([]);
  const [file, setFile] = useState(null);
  const [atype, setAtype] = useState(types[0]?.v);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!parentId) return;
    try { const { data } = await api.get(`/pedagogy/${parentType}/${parentId}/attachments`); setItems(data); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Erreur"); }
  };
  useEffect(() => { if (open) { setFile(null); setAtype(types[0]?.v); load(); } /* eslint-disable-next-line */ }, [open, parentId]);

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("attachment_type", atype);
      await api.post(`/pedagogy/${parentType}/${parentId}/attachments`, fd);
      toast.success("Pièce jointe ajoutée."); setFile(null); await load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Erreur"); }
    finally { setBusy(false); }
  };
  const archive = async (id) => { try { await api.patch(`/pedagogy/attachments/${id}/archive`); await load(); } catch (e) { toast.error("Erreur"); } };
  const reject = async (id) => { const r = prompt("Motif de refus ?"); if (!r) return; try { await api.patch(`/pedagogy/attachments/${id}/reject`, { reason: r }); await load(); } catch (e) { toast.error("Erreur"); } };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="attachments-dialog">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Paperclip className="h-5 w-5" />Pièces jointes</DialogTitle></DialogHeader>
        <div className="space-y-2 max-h-[45vh] overflow-y-auto">
          {items.length === 0 ? <p className="text-sm text-slate-400 py-3 text-center">Aucune pièce jointe.</p>
            : items.map((a) => (
              <div key={a.id} className="flex items-center justify-between gap-2 border-b pb-2" data-testid="attachment-row">
                <div className="min-w-0">
                  <div className="text-sm text-slate-700 truncate">{a.original_filename}</div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <Badge variant="secondary" className="text-[10px]">{a.attachment_type}</Badge>
                    <Badge className={`text-[10px] border-0 ${STC[a.doc_status] || ""}`}>{a.doc_status}</Badge>
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <a href={`${API}/api/pedagogy/attachments/${a.id}/file`} target="_blank" rel="noreferrer"><Button size="icon" variant="ghost" data-testid={`att-preview-${a.id}`}><Eye className="h-4 w-4" /></Button></a>
                  <Button size="icon" variant="ghost" onClick={() => reject(a.id)}><XCircle className="h-4 w-4 text-red-500" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => archive(a.id)}><Archive className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
        </div>
        <div className="border-t pt-3 space-y-2">
          <Select value={atype} onValueChange={setAtype}>
            <SelectTrigger data-testid="att-type"><SelectValue /></SelectTrigger>
            <SelectContent>{types.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
          </Select>
          <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setFile(e.target.files[0])} data-testid="att-file" className="text-sm" />
          <Button onClick={upload} disabled={busy || !file} className="w-full gap-2" data-testid="att-upload">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}Téléverser (PDF/JPG/PNG, 10 Mo max)</Button>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Fermer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function GroupPicker({ value, onChange, testid }) {
  const [groups, setGroups] = useState([]);
  useEffect(() => { api.get("/pedagogy/my-groups").then((r) => { setGroups(r.data); if (!value && r.data[0]) onChange(r.data[0].id); }).catch(() => {}); /* eslint-disable-next-line */ }, []);
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger className="w-64" data-testid={testid}><SelectValue placeholder="Sélectionner un groupe" /></SelectTrigger>
      <SelectContent>{groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.group_name}</SelectItem>)}</SelectContent>
    </Select>
  );
}
